import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Environment } from "../classes/environment";
import { DocumentExchange, DocumentExchangeB, DocumentExchangeCategory, DocumentExchangeCategoryB } from "src/app/classes/document-exchange";
import { catchError, delay, map, Observable, of } from "rxjs";
import { CurrentUserService } from "./current-user.service";


@Injectable( { providedIn: 'root' } )
export class DocumentExchangeService {


	constructor(
		private httpClient: HttpClient,
		@Inject('environment') private environment: Environment,
		private currentUserService: CurrentUserService
	) {}


	getDocumentExchanges(
		page: number,
		memberIds: string[] | null,
		categoryIds: string[]|null,
		isRead: boolean|null = null,
		startTms: number|null = null,
		endTms: number|null = null,
		description = "",
		inbox: boolean|null = null
	): Observable<{ elements: DocumentExchange[], total: number } | null> {
		
		let httpParams = new HttpParams()
			.set( "page", page.toString() )
			.set( "size", "10" );

		if( memberIds && memberIds.length ) {
			httpParams = httpParams.set( "mitgliedIds", memberIds.join( "," ) );
		}
		if( categoryIds ) {
			httpParams = httpParams.set( "agIds", categoryIds.join( "," ) );
		}
		if( isRead !== null ) {
			httpParams = httpParams.set( "gelesen", isRead.toString() );
		}
		if( startTms !== null ) {
			httpParams = httpParams.set( "anfangsDatum", startTms );
		}
		if( endTms !== null ) {
			httpParams = httpParams.set( "endDatum", endTms );
		}
		if( description ) {
			httpParams = httpParams.set( "beschreibung", description );
		}
		if( inbox !== null ) {
			httpParams = httpParams.set( "inbox", inbox );
		}

		// sorting is not working currently in the Backend
		// httpParams = httpParams.set( "sort", "agId,desc" )

		return this.httpClient.get<{
			content: {
				agName: string,
				austausch: DocumentExchangeB,
				dateien: { [key:string]: string }
			}[],
			pageable: { pageNumber: number, pageSize: number },
			totalElements: number
		}>(
			this.environment.backendUrl + 'austausch/search',
			{ params: httpParams }
		).pipe(
			map( response => {

				console.log( "getDocumentExchanges()", response )	  
				if( !response || !response.content ) return null;

				const bAdmin = this.currentUserService.isCurrentUserAdmin();

				return {
					elements: response.content.map( o => {

						const deF = this.convertDocumentExchangeBtoF(o.austausch);
						// deF.bOwn = !!memberId && (deF.memberRecipientId == memberId);
						deF.bOwn = ( bAdmin && !deF.anMitglied ) || ( !bAdmin && deF.anMitglied );
						deF.files = [];
						for( const p in o.dateien ) {
							deF.files.push( { id: p, filename: o.dateien[p] } );
						}
						deF.categoryName = o.agName;
						return deF;

					} ),
					total: response.totalElements
				};

			}),
			catchError( e => of(null) )
		);

	}

	private convertDocumentExchangeBtoF( deB: DocumentExchangeB ): DocumentExchange {

		const deF = new DocumentExchange();
		deF.id = deB.austauschId;
		deF.memberRecipientId = deB.empfaengerMitgliedId;
		deF.category = deB.agId;
		deF.date = deB.austauschDatum;
		deF.dateFirstRead = deB.erstesGelesenDatum;
		deF.description = deB.beschreibung;
		deF.bSeen = deB.gelesen;
		deF.anMitglied = deB.anMitglied;
		deF.memberSenderId = deB.absenderMitgliedId;
		return deF;

	}

	uploadDocumentExchange( description: string, category: string, memberIds: string[], files: File[] ): Observable<boolean> {

		const formData = new FormData();
		for( const file of files ) {
			formData.append( 'files', file, file.name );
		}

		let httpParams = new HttpParams()
			.set( "beschreibung", description )
			.set( "agId", category );

		if( memberIds && memberIds.length ) {
			httpParams = httpParams.set( "empfaengerIds", memberIds.join( "," ) );
		}

		return this.httpClient.post<DocumentExchangeB>(
			this.environment.backendUrl + 'austausch/new',
			formData,
			{ params: httpParams }
		).pipe(
			map( response => {

				if (!response || !response.austausch || !response.austausch.austauschId) {
					console.warn("Keine Austausch-ID in der Antwort");
					return false;
				}

				// Falls "verarbeitet" false ist, aber die ID existiert, dennoch Erfolg zurückgeben
				if (response.verarbeitet === false) {
					console.warn("Backend meldet verarbeitet=false:", response.meldung);
				}
				return true;
			} ),
			catchError( e => {
				return of(false);
			})
		);

	}

	deleteDocumentExchange( id: string ): Observable<boolean> {

		return this.httpClient.delete<{ agId: string }>(
			this.environment.backendUrl + 'austausch/delete',
			{
				params: new HttpParams()
					.set( "austauschId", id )
			}
		).pipe(
			map( response => {
				if( !response || !response.agId ) return false;
				return true;
			} ),
			catchError( e => {
				return of(false);
			})
		);

	}

	setViewedStatus( id: string, b: boolean ): Observable<boolean> {

		return this.httpClient.put<boolean>(
			this.environment.backendUrl + 'austausch/gelesen',
			{},
			{
				params: new HttpParams()
					.set( "austauschId", id )
					.set( "gelesen", b )
			}
		).pipe(
			map( response => {
				if( !response ) return false;
				return true;
			} ),
			catchError( e => {
				return of(false);
			})
		);

	}



	getDocumentExchangeCategories(): Observable<DocumentExchangeCategory[] | null> {

		return this.httpClient.get<DocumentExchangeCategoryB[]>(
			this.environment.backendUrl + 'austauschgruppe/all',
			{},
		).pipe(
			map( categories => {

				if( !categories ) return null;

				return categories
					.map( c => this.convertDocumentExchangeCategoryBtoF( c ) )
					.sort( (a,b) => a.name.localeCompare( b.name ) );

			}),
			catchError( e => of(null) )
		);

	}

	saveDocumentExchangeCategory( documentExchangeCategory: DocumentExchangeCategory ): Observable<DocumentExchangeCategory | null> {

		return documentExchangeCategory.id
			? this.updateDocumentExchangeCategory( documentExchangeCategory )
			: this.createDocumentExchangeCategory( documentExchangeCategory );

	}

	private createDocumentExchangeCategory( documentExchangeCategory: DocumentExchangeCategory ): Observable<DocumentExchangeCategory | null> {

		return this.httpClient.post<DocumentExchangeCategoryB>(
			this.environment.backendUrl + 'austauschgruppe/new',
			{},
			{
				params: new HttpParams()
					.set( "agName", documentExchangeCategory.name )
					.set( "agBeschreibung", documentExchangeCategory.description )
					.set("erinerungsmail", documentExchangeCategory.email.toString())
			}
		).pipe(
			map( category => {

				if( !category || !category.agId ) return null;

				return this.convertDocumentExchangeCategoryBtoF( category );

			}),
			catchError( e => of(null) )
		);
	}

	private updateDocumentExchangeCategory( documentExchangeCategory: DocumentExchangeCategory ): Observable<DocumentExchangeCategory | null> {

		return this.httpClient.put<DocumentExchangeCategoryB>(
			this.environment.backendUrl + 'austauschgruppe/update',
			this.convertDocumentExchangeCategoryFtoB( documentExchangeCategory ),
		).pipe(
			map( category => {

				if( !category || !category.agId ) return null;

				return this.convertDocumentExchangeCategoryBtoF( category );

			}),
			catchError( e => of(null) )
		);

	}

	private convertDocumentExchangeCategoryBtoF( documentExchangeCategoryB: DocumentExchangeCategoryB ): DocumentExchangeCategory {

		const documentExchangeCategoryF = new DocumentExchangeCategory();
		documentExchangeCategoryF.id = documentExchangeCategoryB.agId;
		documentExchangeCategoryF.description = documentExchangeCategoryB.agBeschreibung;
		documentExchangeCategoryF.name = documentExchangeCategoryB.agName;
		documentExchangeCategoryF.email = documentExchangeCategoryB.erinerungsmail;
		documentExchangeCategoryF.agSubmenu = documentExchangeCategoryB.agSubmenu ?? false;
		return documentExchangeCategoryF;
	}

	private convertDocumentExchangeCategoryFtoB( documentExchangeCategoryF: DocumentExchangeCategory ): DocumentExchangeCategoryB {

		const documentExchangeCategoryB = new DocumentExchangeCategoryB();
		documentExchangeCategoryB.agId = documentExchangeCategoryF.id;
		documentExchangeCategoryB.agBeschreibung = documentExchangeCategoryF.description;
		documentExchangeCategoryB.agName = documentExchangeCategoryF.name;
		documentExchangeCategoryB.erinerungsmail = documentExchangeCategoryF.email;
		documentExchangeCategoryB.agSubmenu = documentExchangeCategoryF.agSubmenu;
		return documentExchangeCategoryB;
	}

	deleteDocumentExchangeCategory( id: string ): Observable<boolean> {

		return this.httpClient.delete<{ agId: string }>(
			this.environment.backendUrl + 'austauschgruppe/delete',
			{
				params: new HttpParams().set( "agId", id )
			}
		).pipe(
			map( response => {
				if( !response || !response.agId ) return false;
				return true;
			} ),
			catchError( e => {
				return of(false);
			})
		);

	}


	getDocumentExchangeCategoriesOfMember( memberId: string ): Observable<DocumentExchangeCategory[] | null> {

		return this.httpClient.get<DocumentExchangeCategoryB[]>(
			this.environment.backendUrl + 'austauschgruppe/all/mitglied',
			{ params: new HttpParams().set( "mitgliedId", memberId ) },
		).pipe(
			map( categories => {

				if( !categories ) return null;

				return categories
					.map( c => this.convertDocumentExchangeCategoryBtoF( c ) )
					.sort( (a,b) => a.name.localeCompare( b.name ) );

			}),
			catchError( e => of(null) )
		);

	}

	removeDocumentExchangeCategoryOfMember( memberId: string, categoryId: string ): Observable<boolean> {

		return this.httpClient.delete<boolean>(
			this.environment.backendUrl + 'austauschgruppe/delete/mitglied',
			{
				params: new HttpParams()
					.set( "mitgliedId", memberId )
					.set( "agId", categoryId )
			},
		).pipe(
			map( b => {
				return !!b;
			}),
			catchError( e => of(false) )
		);

	}

	addDocumentExchangeCategoryToMember( memberId: string, categoryId: string ): Observable<boolean> {

		return this.httpClient.post<{ agmId: string, agId: string, mitgliedId: string }>(
			this.environment.backendUrl + 'austauschgruppe/assign/mitglied',
			{},
			{
				params: new HttpParams()
					.set( "mitgliedId", memberId )
					.set( "agId", categoryId )
			},
		).pipe(
			map( result => {
				return !!(result && result.agmId);
			}),
			catchError( e => of(false) )
		);

	}


	getDocumentExchangeCategoriesOfUser( userId: string ): Observable<DocumentExchangeCategory[] | null> {

		return this.httpClient.get<DocumentExchangeCategoryB[]>(
			this.environment.backendUrl + 'austauschgruppe/all/benutzer',
			{ params: new HttpParams().set( "ebeId", userId ) },
		).pipe(
			map( categories => {

				if( !categories ) return null;

				return categories
					.map( c => this.convertDocumentExchangeCategoryBtoF( c ) )
					.sort( (a,b) => a.name.localeCompare( b.name ) );

			}),
			catchError( e => of(null) )
		);

	}

	removeDocumentExchangeCategoryOfUser( userId: string, categoryId: string ): Observable<boolean> {

		return this.httpClient.delete<boolean>(
			this.environment.backendUrl + 'austauschgruppe/delete/benutzer',
			{
				params: new HttpParams()
					.set( "ebeId", userId )
					.set( "agId", categoryId )
			},
		).pipe(
			map( b => {
				return !!b;
			}),
			catchError( e => of(false) )
		);

	}

	addDocumentExchangeCategoryToUser( userId: string, categoryId: string ): Observable<boolean> {

		return this.httpClient.post<{ agbId: string, agId: string, mitgliedId: string }>(
			this.environment.backendUrl + 'austauschgruppe/assign/benutzer',
			{},
			{
				params: new HttpParams()
					.set( "ebeId", userId )
					.set( "agId", categoryId )
			},
		).pipe(
			map( result => {
				return !!(result && result.agbId);
			}),
			catchError( e => of(false) )
		);

	}

}